import React, {
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import s from './Astro.module.css';
import type { AstroRef, AstroProps, MoveOptions } from './types';
import { ASTRO_SIZE } from './config';
import { useAvatarSetup, useMovement, useDotAnimation } from './hooks';
import { DotTrail } from './components/DotTrail';
import { AvatarEvent, AvatarState } from '@wix/aria-ai-avatar';

export const AiAssistantAvatar = forwardRef<AstroRef, AstroProps>(
  ({ mode, variant }, ref: Ref<AstroRef> | null) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const typingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [isTyping, setIsTyping] = useState(false);

    const [showBg, setShowBg] = useState(false);

    const { AriaAvatar, avatarHidden, setAvatarHidden } = useAvatarSetup({
      mode,
      variant,
    });

    const { setCenter } = useMovement(
      wrapperRef,
      ASTRO_SIZE.WIDTH,
      ASTRO_SIZE.HEIGHT,
    );

    const {
      leadDotRef,
      trailRefs,
      hideDots,
      moveFromOffsetWithDots,
      moveToOffsetWithDots,
    } = useDotAnimation(setCenter, setAvatarHidden, AriaAvatar.setState);

    const onTravelHomeFrom = async (
      x: number,
      y: number,
      options?: MoveOptions,
    ) => {
      const pos = { x, y };
      await moveFromOffsetWithDots(pos, options);
    };

    const onTravelAwayTo = async (
      x: number,
      y: number,
      options?: MoveOptions,
    ) => {
      const pos = { x, y };
      await moveToOffsetWithDots(pos, options);
    };

    const onMouseMove = (clientX: number, clientY: number) => {
      if (isTyping) {
        return;
      }

      AriaAvatar.lookAt(clientX, clientY);
    };

    const onUserTyping = (caretX: number, caretY: number) => {
      try {
        setIsTyping(true);
        AriaAvatar.lookAt(caretX, caretY);

        if (typingTimeout.current) {
          clearTimeout(typingTimeout.current);
        }

        typingTimeout.current = setTimeout(() => {
          setIsTyping(false);
        }, 500);
      } catch (err) {
        console.error('[Astro] Error tracking typing:', err);
      }
    };

    useEffect(() => {
      return () => {
        if (typingTimeout.current) {
          clearTimeout(typingTimeout.current);
        }
      };
    }, []);

    const hideAvatar = ({ withDots = false }: { withDots?: boolean } = {}) => {
      setAvatarHidden(true);

      if (withDots) {
        hideDots();
      }
    };

    const showAvatar = () => {
      setAvatarHidden(false);
    };

    useImperativeHandle(ref, () => ({
      trigger: {
        shrink: () => {
          if (AriaAvatar.getState() === AvatarState.SMALL) {
            return AriaAvatar.setState(AvatarState.IDLE);
          }
          return AriaAvatar.setState(AvatarState.SMALL);
        },
        smallLoader: () => {
          if (AriaAvatar.getState() === AvatarState.SMALL) {
            return AriaAvatar.setState(AvatarState.SMALL_LOADER);
          }
          return AriaAvatar.setState(AvatarState.SMALL);
        },
        pulse: () => Promise.resolve(),
        idle: async () => {
          if (AriaAvatar.getState() === AvatarState.SMALL_LOADER) {
            await AriaAvatar.setState(AvatarState.SMALL);
          }
          return AriaAvatar.setState(AvatarState.IDLE);
        },
        undo: () => AriaAvatar.fireEvent(AvatarEvent.UNDO_EVENT),
        boredom: () => {
          if (AriaAvatar.getState() === AvatarState.SLEEPING) {
            return AriaAvatar.setState(AvatarState.IDLE);
          }
          return AriaAvatar.setState(AvatarState.SLEEPING);
        },
        bigLoader: () => {
          if (AriaAvatar.getState() === AvatarState.BIG_LOADER) {
            return AriaAvatar.setState(AvatarState.IDLE);
          }
          return AriaAvatar.setState(AvatarState.BIG_LOADER);
        },
        ideaSpark: () => AriaAvatar.fireEvent(AvatarEvent.IDEA_SPARK),
        publish: () => AriaAvatar.fireEvent(AvatarEvent.PUBLISH_EVENT),
      },
      avatarHidden,
      wrapper: wrapperRef.current,
      getState: AriaAvatar.getState,

      onMouseMove,
      onUserTyping,
      onTravelHomeFrom,
      onTravelAwayTo,

      changeAstroColor: (color: string) => {
        AriaAvatar.setAvatarColor(color);
      },

      toggleAvatarBackground: () => {
        if (showBg) {
          setShowBg(false);
          AriaAvatar.hideBg();
        } else {
          setShowBg(true);
          AriaAvatar.showBg();
        }
      },

      hide: hideAvatar,
      show: showAvatar,
    }));

    return (
      <div className={s.avatar}>
        <DotTrail leadDotRef={leadDotRef} trailRefs={trailRefs} />

        <div
          ref={wrapperRef}
          className={s.astro}
          style={{
            opacity: avatarHidden ? 0 : 1,
          }}
        >
          <div style={{ width: ASTRO_SIZE.WIDTH, height: ASTRO_SIZE.HEIGHT }}>
            <AriaAvatar.AriaAvatar />
          </div>
        </div>
      </div>
    );
  },
);
